import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import type { LineSeriesOption } from 'echarts';
import { debounce } from 'lodash';

interface DataItem {
  月份: string;
  产品名称: string;
  SPU名称: string;
  销售数量: number;
  去年同期销售数量: number;
  人工预测销量: number;
  机器预测销量: number;
}

interface SkuSalesForecastProps {
  data: DataItem[];
  loading?: boolean;
}

// 线条颜色
const LINE_COLORS = {
  销售数量: '#5470C6',
  去年同期销售数量: '#FF0000',
  人工预测销量: '#90EE90',
  机器预测销量: '#FFDAB9'
} as const;

// 线条样式
const LINE_TYPES = {
  销售数量: 'solid',
  去年同期销售数量: 'solid',
  人工预测销量: 'dotted',
  机器预测销量: 'dotted'
} as const;

// 图表数据项 - 数量类指标
const CHART_METRICS = [
  '销售数量',
  '去年同期销售数量',
  '人工预测销量',
  '机器预测销量'
] as const;

export const SkuSalesForecast: React.FC<SkuSalesForecastProps> = ({ data, loading }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // 处理数据
  const processData = (rawData: DataItem[]) => {
    if (!rawData || rawData.length === 0) return { months: [], series: [], productInfo: null };

    // 按月份排序
    const sortedData = [...rawData].sort((a, b) => a.月份.localeCompare(b.月份));
    const months = sortedData.map(item => item.月份);

    // 创建数量系列数据
    const series: LineSeriesOption[] = CHART_METRICS.map(metric => ({
      name: metric,
      type: 'line',
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: {
        type: LINE_TYPES[metric],
        width: 2
      },
      itemStyle: {
        color: LINE_COLORS[metric]
      },
      data: sortedData.map(item => item[metric])
    }));

    return {
      months,
      series,
      productInfo: sortedData[0] || null
    };
  };

  // 初始化图表
  const initChart = () => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const { months, series, productInfo } = processData(data);

    const option: echarts.EChartsOption = {
      title: {
        text: '',
        left: 'center',
        top: 0,
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        formatter: (params: any[]) => {
          if (!params[0] || !productInfo) return '';

          let result = `
            <div class="font-bold mb-2">${params[0].axisValue}</div>
            <div class="mb-2">
             <div>产品名称：${productInfo.产品名称 || '-'}</div>
             <div>SPU名称：${productInfo.SPU名称 || '-'}</div>
            </div>
          `;

          params.forEach(param => {
            const marker = `<span style="display:inline-block;margin-right:4px;border-radius:50%;width:10px;height:10px;background-color:${param.color};"></span>`;
            const value = param.value.toLocaleString();
            result += `<div>${marker}${param.seriesName}：${value}</div>`;
          });

          return result;
        }
      },
      legend: {
        data: [...CHART_METRICS] as unknown as string[],
        top: 30,
        left: 'center',
        textStyle: {
          fontSize: 12
        }
      },
      grid: {
        left: '3%',
        right: '8%',
        bottom: '3%',
        containLabel: true,
        top: 80
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: months,
        axisLabel: {
          rotate: 45,
          formatter: (value: string) => {
            // 格式化日期显示，如 "2025-06" 显示为 "2025年6月"
            const [year, month] = value.split('-');
            return `${year}年${parseInt(month)}月`;
          }
        }
      },
      yAxis: {
        type: 'value',
        name: '销售数量',
        axisLabel: {
          formatter: (value: number) => {
            return value.toLocaleString();
          }
        },
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        }
      },
      series
    };

    chartInstance.current.setOption(option);
  };

  // 处理窗口大小变化
  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    }, 300);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  // 更新图表数据
  useEffect(() => {
    if (data && data.length > 0) {
      initChart();
    }
  }, [data]);

  // 销毁图表实例
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full h-full min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div ref={chartRef} className="w-full h-[500px]" />
  );
};

export default SkuSalesForecast;